import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';

const UsersManagement = () => {
  const [activeItem, setActiveItem] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirmModal, setConfirmModal] = useState(null); // { action, userId, role, userName }
  const [successModal, setSuccessModal] = useState(null); // { message }

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('get_all_phase0_users');
      if (rpcError) throw rpcError;
      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const { userId, role, userName } = confirmModal;
    setConfirmModal(null);
    try {
      setActionLoading(userId);
      setError(null);
      const { error: rpcError } = await supabase.rpc('approve_user', { p_user_id: userId, p_role: role });
      if (rpcError) throw rpcError;
      try {
        await supabase.functions.invoke('send-user-approval-email', { body: { userId, role } });
      } catch (_) {}
      await loadUsers();
      setSuccessModal({ message: `${userName} has been approved. An approval email has been sent.`, type: 'approve' });
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBan = async () => {
    const { userId, role, userName } = confirmModal;
    setConfirmModal(null);
    try {
      setActionLoading(userId);
      setError(null);
      const { error: rpcError } = await supabase.rpc('ban_user', { p_user_id: userId, p_role: role });
      if (rpcError) throw rpcError;
      await loadUsers();
      setSuccessModal({ message: `${userName} has been ${role === 'pending' ? 'rejected' : 'banned'} successfully.`, type: 'ban' });
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(user => {
    if (filterRole !== 'all' && user.role !== filterRole) return false;
    if (filterStatus === 'approved' && !user.approved) return false;
    if (filterStatus === 'pending' && user.approved) return false;
    return true;
  });

  const formatRole = (role) => {
    if (role === 'league_admin') return 'League Admin';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="dashboard-layout">
      <DashboardSidebar activeItem={activeItem} setActiveItem={setActiveItem} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="dashboard-main">
        <DashboardHeader toggleSidebar={() => setSidebarOpen(!sidebarOpen)} title="Users Management" subtitle="Manage coaches, trainers, and league administrators" />

        {/* Confirm Modal */}
        {confirmModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: confirmModal.action === 'approve' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
              }}>
                {confirmModal.action === 'approve' ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                )}
              </div>
              <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>
                {confirmModal.action === 'approve' ? 'Approve User?' : confirmModal.action === 'reject' ? 'Reject User?' : 'Ban User?'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
                {confirmModal.action === 'approve'
                  ? <>Are you sure you want to approve <span style={{ color: '#fff', fontWeight: '600' }}>{confirmModal.userName}</span>? They will be able to submit standouts.</>
                  : confirmModal.action === 'reject'
                  ? <>Are you sure you want to reject <span style={{ color: '#fff', fontWeight: '600' }}>{confirmModal.userName}</span>?</>
                  : <>Are you sure you want to ban <span style={{ color: '#fff', fontWeight: '600' }}>{confirmModal.userName}</span>? They will lose access.</>
                }
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setConfirmModal(null)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={confirmModal.action === 'approve' ? handleApprove : handleBan}
                  style={{
                    flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                    background: confirmModal.action === 'approve' ? '#22c55e' : '#ef4444',
                    color: '#fff',
                  }}
                >
                  {confirmModal.action === 'approve' ? 'Approve' : confirmModal.action === 'reject' ? 'Reject' : 'Ban'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {successModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '360px', width: '90%', textAlign: 'center' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: successModal.type === 'approve' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={successModal.type === 'approve' ? '#22c55e' : '#ef4444'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>Done!</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>{successModal.message}</p>
              <button onClick={() => setSuccessModal(null)} style={{ background: '#f3b129', color: '#000', border: 'none', borderRadius: '8px', padding: '10px 28px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
                Got it
              </button>
            </div>
          </div>
        )}

        <div className="standouts-screen">
          {error && (
            <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: 'rgba(255,255,255,0.8)' }}>Role</label>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '14px' }}>
                <option value="all">All Roles</option>
                <option value="coach">Coach</option>
                <option value="trainer">Trainer</option>
                <option value="league_admin">League Admin</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: 'rgba(255,255,255,0.8)' }}>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '14px' }}>
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
              <button onClick={loadUsers} disabled={loading} style={{ padding: '8px 16px', background: 'rgba(212,165,116,0.1)', border: '1px solid rgba(212,165,116,0.3)', borderRadius: '6px', color: '#d4a574', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                {loading ? 'Refreshing...' : '↻ Refresh'}
              </button>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                  {['Name', 'Email', 'Phone', 'Role', 'Status', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={tableHeaderStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      {[140, 200, 90, 70, 80, 80, 120].map((w, j) => (
                        <td key={j} style={{ padding: '16px' }}>
                          <div style={{ height: '14px', width: `${w}px`, borderRadius: '6px', background: 'rgba(255,255,255,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>No users found</td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', opacity: actionLoading === user.user_id ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      <td style={tableCellStyle}>{user.first_name} {user.last_name}</td>
                      <td style={tableCellStyle}>{user.email}</td>
                      <td style={tableCellStyle}>{user.phone || '-'}</td>
                      <td style={tableCellStyle}>
                        <span style={{ padding: '4px 8px', background: 'rgba(212,165,116,0.2)', color: '#d4a574', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                          {formatRole(user.role)}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ padding: '4px 8px', background: user.approved ? 'rgba(34,197,94,0.2)' : 'rgba(216,161,52,0.2)', color: user.approved ? '#22c55e' : '#d8a134', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                          {user.approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td style={tableCellStyle}>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          {!user.approved && (
                            <button
                              onClick={() => setConfirmModal({ action: 'approve', userId: user.user_id, role: user.role, userName: `${user.first_name} ${user.last_name}` })}
                              disabled={actionLoading === user.user_id}
                              style={{ padding: '6px 12px', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '4px', color: '#22c55e', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              {actionLoading === user.user_id ? '...' : 'Approve'}
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmModal({ action: user.approved ? 'ban' : 'reject', userId: user.user_id, role: user.role, userName: `${user.first_name} ${user.last_name}` })}
                            disabled={actionLoading === user.user_id}
                            style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px', color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                          >
                            {actionLoading === user.user_id ? '...' : user.approved ? 'Ban' : 'Reject'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
            {[{ label: 'Total Users', value: users.length, color: '#fff' }, { label: 'Approved', value: users.filter(u => u.approved).length, color: '#22c55e' }, { label: 'Pending', value: users.filter(u => !u.approved).length, color: '#d8a134' }].map(s => (
              <div key={s.label}>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '600', color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
};

const tableHeaderStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tableCellStyle = { padding: '16px', fontSize: '14px', color: 'rgba(255,255,255,0.9)' };

export default UsersManagement;
